/* eslint-disable @typescript-eslint/no-use-before-define -- helper components are declared below the entry component; hoisting is compiler-safe. */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGetProjectIntegration,
  useSetProjectMapping,
  useClearProjectMapping,
  useGetColoopWorkspaces,
  useGetColoopWorkspaceProjects,
  useGetOrgIntegrations,
  usePushAllConversations,
  useGetProjectExports,
  type ConversationExport,
  type IntegrationRecord,
} from '@/reactQuery/integrations';
import useOrganizationRoles from '@/hooks/useOrganizationRoles';
import { useUser } from '@/hooks/useUser';

interface Props {
  organizationId: string;
  projectId: string;
}

/**
 * Embedded on the project page. Renders one of:
 *  1. "CoLoop is not connected on this org → go to Settings" link
 *  2. The current mapping with Unlink button
 *  3. A workspace + project picker for setting the mapping
 *
 * Push buttons live in TELV1-235/236; this component just owns the mapping.
 */
function CoLoopProjectMapping({ organizationId, projectId }: Props) {
  const orgIntegrations = useGetOrgIntegrations(organizationId);
  const coloopOrg = orgIntegrations.data?.find((i) => i.provider === 'coloop');
  const isConnected = coloopOrg?.status === 'connected';

  const mappingQuery = useGetProjectIntegration(projectId);
  const setMapping = useSetProjectMapping(projectId);
  const clearMapping = useClearProjectMapping(projectId);

  const [pickerWorkspace, setPickerWorkspace] = useState<string>('');
  const [pickerProject, setPickerProject] = useState<string>('');
  // Changing the workspace MUST clear any staged project — the old
  // selection is by-id and would silently point at a project in a
  // different workspace after the change.
  useEffect(() => {
    setPickerProject('');
  }, [pickerWorkspace]);

  const workspacesQuery = useGetColoopWorkspaces(organizationId, isConnected);
  const projectsQuery = useGetColoopWorkspaceProjects(
    organizationId,
    pickerWorkspace,
    Boolean(pickerWorkspace),
  );

  const workspacesById = useMemo(() => {
    const m = new Map<string, { id: string; name: string }>();
    (workspacesQuery.data ?? []).forEach((w) => m.set(w.id, w));
    return m;
  }, [workspacesQuery.data]);

  const projectsById = useMemo(() => {
    const m = new Map<string, { id: string; name: string }>();
    (projectsQuery.data ?? []).forEach((p) => m.set(p.id, p));
    return m;
  }, [projectsQuery.data]);

  if (orgIntegrations.isLoading || mappingQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading CoLoop status…
      </div>
    );
  }

  if (!isConnected) {
    const isExpired = coloopOrg?.status === 'expired';
    const isRevoked = coloopOrg?.status === 'revoked';
    // Both expired and revoked deserve visible warning styling — a
    // previously-working link is now blocked and the user needs to act.
    const wrapperClass =
      isExpired || isRevoked
        ? 'text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3'
        : 'text-sm text-muted-foreground';
    let message = 'CoLoop is not connected for this organization.';
    if (isExpired) {
      message =
        'CoLoop has revoked or expired the credentials for this organization. Pushing is paused until you reconnect.';
    } else if (isRevoked) {
      message = 'CoLoop was disconnected for this organization. Reconnect to resume pushing.';
    }
    return (
      <p className={wrapperClass}>
        {message}&nbsp;
        <a href={`/organization/${organizationId}/integrations`} className="text-primary underline">
          {isExpired || isRevoked ? 'Reconnect in Settings' : 'Connect it in Settings'}
        </a>
        &nbsp;to push transcripts from this project.
      </p>
    );
  }

  if (mappingQuery.data) {
    return (
      <LinkedView
        organizationId={organizationId}
        projectId={projectId}
        mapping={mappingQuery.data}
        coloop={coloopOrg}
        onUnlink={() => clearMapping.mutate()}
        isUnlinking={clearMapping.isPending}
      />
    );
  }

  const canSave = Boolean(pickerWorkspace) && Boolean(pickerProject) && !setMapping.isPending;
  const handleSave = () => {
    const ws = workspacesById.get(pickerWorkspace);
    const proj = projectsById.get(pickerProject);
    if (!ws || !proj) return;
    setMapping.mutate(
      {
        organizationId,
        workspaceId: ws.id,
        workspaceName: ws.name,
        projectId: proj.id,
        projectName: proj.name,
      },
      {
        onError: () => toast.error('Could not link this project. Please try again.'),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 max-w-xl">
      <div className="text-sm font-medium">Link this project to a CoLoop project</div>

      <Select value={pickerWorkspace} onValueChange={setPickerWorkspace}>
        <SelectTrigger>
          <SelectValue placeholder="Select a CoLoop workspace…" />
        </SelectTrigger>
        <SelectContent>
          {workspacesQuery.isLoading && (
            <SelectItem disabled value="__ws_loading">
              Loading workspaces…
            </SelectItem>
          )}
          {!workspacesQuery.isLoading &&
            !workspacesQuery.isError &&
            (workspacesQuery.data ?? []).length === 0 && (
              <SelectItem disabled value="__ws_empty">
                No workspaces found in CoLoop
              </SelectItem>
            )}
          {workspacesQuery.data?.map((w) => (
            <SelectItem key={w.id} value={w.id}>
              {w.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {workspacesQuery.isError && (
        <p className="text-xs text-destructive">
          Couldn&apos;t load your CoLoop workspaces. Please try again.
        </p>
      )}

      <Select
        value={pickerProject}
        disabled={!pickerWorkspace || projectsQuery.isLoading}
        onValueChange={setPickerProject}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={pickerWorkspace ? 'Select a CoLoop project…' : 'Pick a workspace first'}
          />
        </SelectTrigger>
        <SelectContent>
          {projectsQuery.isLoading && (
            <SelectItem disabled value="__proj_loading">
              Loading projects…
            </SelectItem>
          )}
          {!projectsQuery.isLoading &&
            !projectsQuery.isError &&
            pickerWorkspace &&
            (projectsQuery.data ?? []).length === 0 && (
              <SelectItem disabled value="__proj_empty">
                No projects in this workspace
              </SelectItem>
            )}
          {projectsQuery.data?.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {projectsQuery.isError && (
        <p className="text-xs text-destructive">
          Couldn&apos;t load projects for this workspace. Please try again.
        </p>
      )}

      <div className="flex items-center gap-2 mt-1">
        <Button type="button" onClick={handleSave} disabled={!canSave}>
          {setMapping.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Link project
        </Button>
        {setMapping.isPending && <span className="text-xs text-muted-foreground">Saving…</span>}
      </div>
    </div>
  );
}

/**
 * Copy for the "all conversations were skipped" case — every candidate
 * conversation either already has a succeeded export or is currently
 * in-flight, so nothing new got enqueued. Split from the message below
 * because the wording is different when everything skipped vs when some
 * still went through.
 */
function buildAllSkippedMessage(data: {
  skippedSucceededCount?: number;
  skippedInFlightCount?: number;
}): string {
  const succ = data.skippedSucceededCount ?? 0;
  const inflight = data.skippedInFlightCount ?? 0;
  if (succ > 0 && inflight === 0) {
    return `Nothing to push — all ${succ} conversation${succ === 1 ? ' has' : 's have'} already been sent to CoLoop.`;
  }
  if (inflight > 0 && succ === 0) {
    return `Nothing to push — a previous push is still processing (${inflight} in flight).`;
  }
  if (succ > 0 && inflight > 0) {
    return `Nothing to push — ${succ} already sent, ${inflight} still processing.`;
  }
  return 'Nothing to push.';
}

/**
 * Copy for the mixed case — some conversations were enqueued, others
 * were skipped as already-sent or in-flight. Just tells the user why
 * the number in the toast is smaller than the number of conversations
 * they had.
 */
function buildPartialSkipMessage(data: {
  enqueuedCount: number;
  skippedSucceededCount?: number;
  skippedInFlightCount?: number;
}): string {
  const parts: string[] = [];
  parts.push(`Queued ${data.enqueuedCount} new push${data.enqueuedCount === 1 ? '' : 'es'}.`);
  if ((data.skippedSucceededCount ?? 0) > 0) {
    parts.push(`Skipped ${data.skippedSucceededCount} already sent to CoLoop.`);
  }
  if ((data.skippedInFlightCount ?? 0) > 0) {
    parts.push(`Skipped ${data.skippedInFlightCount} still processing.`);
  }
  return parts.join(' ');
}

function LinkedView({
  organizationId: _organizationId,
  projectId,
  mapping,
  coloop,
  onUnlink,
  isUnlinking,
}: {
  organizationId: string;
  projectId: string;
  mapping: { remote: { workspaceName?: string; projectName?: string; projectId: string } };
  /**
   * The CoLoop org-integration record, used to render "Connected by
   * <name>" and to decide whether to warn on Unlink (when the caller
   * did not personally connect).
   */
  coloop?: IntegrationRecord;
  onUnlink: () => void;
  isUnlinking: boolean;
}) {
  const pushAll = usePushAllConversations(projectId);
  // useGetProjectExports now polls adaptively — 4s while any row is
  // pending/in_flight, off otherwise. No hard-coded interval here.
  const exports = useGetProjectExports(projectId);
  const { isUserOwner, isUserAdmin } = useOrganizationRoles();
  const { user } = useUser();
  const canManage = isUserOwner || isUserAdmin;
  // Whose CoLoop identity currently owns the stored tokens. Prefer the
  // most-recent (re)connector; fall back to the original connector for
  // integrations that never went through a reconnect. Force-stringify
  // both sides of the comparison — one comes from the DTO as a plain
  // string, the other from `useUser` which serialises ObjectIds
  // through the Mongo layer; without the coercion `"5f9b…"` !== an
  // ObjectId with the same hex would silently open the confirm dialog
  // for the real connector.
  const connectorId =
    coloop?.lastConnectedBy?.toString() ??
    coloop?.createdBy?.toString() ??
    null;
  const connectorName =
    coloop?.lastConnectedByName ?? coloop?.createdByName ?? null;
  const isConnector =
    !!connectorId && !!user?._id && user._id.toString() === connectorId;
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);

  const handleUnlinkClick = () => {
    if (!canManage) return;
    // Two direct-action paths, one confirm-first path:
    //  • The original connector or the last reconnector: this IS their
    //    CoLoop identity — no surprise, unlink immediately.
    //  • Any OWNER: full org control, they get to make the call
    //    without a modal even if the tokens aren't theirs.
    //  • Any other admin: confirm modal names the connector so they
    //    know whose workspace they're about to disconnect from.
    if (isConnector || isUserOwner) {
      onUnlink();
    } else {
      setConfirmUnlinkOpen(true);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm">
          Linked to CoLoop project{' '}
          <strong>{mapping.remote.projectName ?? mapping.remote.projectId}</strong>
          {mapping.remote.workspaceName && (
            <>
              {' '}
              in workspace <strong>{mapping.remote.workspaceName}</strong>
            </>
          )}
          .
        </span>
        <Button
          type="button"
          onClick={() =>
            pushAll.mutate(undefined, {
              onError: () => toast.error('Could not start the push. Please try again.'),
            })
          }
          disabled={pushAll.isPending}
        >
          {pushAll.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Push all conversations to CoLoop
        </Button>
        {canManage && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isUnlinking}
            onClick={handleUnlinkClick}
          >
            {isUnlinking && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            Unlink
          </Button>
        )}
      </div>

      {connectorName && (
        <p className="text-xs text-muted-foreground">
          Pushes flow through <strong>{connectorName}</strong>&apos;s CoLoop account
          {coloop?.lastConnectedAt && (
            <>
              {' '}
              (connected {new Date(coloop.lastConnectedAt).toLocaleDateString()})
            </>
          )}
          .
        </p>
      )}

      <AlertDialog open={confirmUnlinkOpen} onOpenChange={setConfirmUnlinkOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink this project from CoLoop?</AlertDialogTitle>
            <AlertDialogDescription>
              This CoLoop integration was set up by{' '}
              <strong>{connectorName ?? 'another admin'}</strong>. Unlinking
              removes the connection between this Tellet project and{' '}
              {connectorName ? `${connectorName}'s` : 'their'} CoLoop workspace —
              existing pushes stay in CoLoop. You&apos;ll need to re-link to
              push new transcripts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmUnlinkOpen(false);
                onUnlink();
              }}
            >
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {pushAll.data && pushAll.data.enqueuedCount === 0 && (
        <p className="text-xs text-muted-foreground bg-muted rounded p-2">
          {pushAll.data.reason ?? buildAllSkippedMessage(pushAll.data)}
        </p>
      )}
      {pushAll.data &&
        pushAll.data.enqueuedCount > 0 &&
        ((pushAll.data.skippedSucceededCount ?? 0) > 0 ||
          (pushAll.data.skippedInFlightCount ?? 0) > 0) && (
          <p className="text-xs text-muted-foreground bg-muted rounded p-2">
            {buildPartialSkipMessage(pushAll.data)}
          </p>
        )}

      <ExportsTable exports={exports.data ?? []} loading={exports.isLoading} />
    </div>
  );
}

function ExportsTable({ exports, loading }: { exports: ConversationExport[]; loading: boolean }) {
  if (loading && exports.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading export history…
      </div>
    );
  }
  if (exports.length === 0) {
    return <p className="text-xs text-muted-foreground">No CoLoop pushes yet for this project.</p>;
  }

  const counts = exports.reduce<Record<string, number>>((acc, e) => {
    acc[e.status] = (acc[e.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="rounded border">
      <div className="flex flex-wrap gap-3 p-3 border-b text-xs">
        {(['pending', 'in_flight', 'succeeded', 'failed', 'skipped'] as const).map((s) => (
          <span key={s} className="flex items-center gap-1">
            <ExportStatusBadge status={s} /> {counts[s] ?? 0}
          </span>
        ))}
      </div>
      {/*
       * Scrollable list — a batch of 100 conversations produces 100 rows;
       * hard-slicing to 10 with no pager left the tail invisible. Cap the
       * viewport at ~10 row-heights so the modal stays a reasonable size
       * and let the user scroll the rest.
       */}
      <div className="max-h-[320px] overflow-y-auto">
        <table className="w-full text-xs">
          {/*
           * Sticky lives on the <th>, not the <thead> — browsers strip
           * position:sticky off table-parent elements. Also using an
           * explicit `bg-slate-100` (opaque) rather than `bg-muted`,
           * whose CSS-variable value in the dashboard theme is very
           * near white and reads as transparent behind the scrolled
           * rows.
           */}
          <thead>
            <tr>
              <th className="text-left p-2 bg-slate-100 sticky top-0 z-10 border-b">
                Conversation
              </th>
              <th className="text-left p-2 bg-slate-100 sticky top-0 z-10 border-b">Status</th>
              <th className="text-left p-2 bg-slate-100 sticky top-0 z-10 border-b">Attempts</th>
              <th className="text-left p-2 bg-slate-100 sticky top-0 z-10 border-b">Enqueued</th>
              <th className="text-left p-2 bg-slate-100 sticky top-0 z-10 border-b">CoLoop link</th>
            </tr>
          </thead>
          <tbody>
            {exports.map((e) => (
              <tr key={e._id} className="border-t">
                <td className="p-2 font-mono text-[10px]">{e.conversationId.slice(-8)}</td>
                <td className="p-2">
                  <ExportStatusBadge status={e.status} />
                </td>
                <td className="p-2">{e.attempts}</td>
                <td className="p-2 text-muted-foreground">
                  {new Date(e.enqueuedAt).toLocaleString()}
                </td>
                <td className="p-2">
                  {e.remote?.deepLink ? (
                    <a
                      href={e.remote.deepLink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline"
                    >
                      Open
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-2 text-xs text-muted-foreground border-t">
        {exports.length} export{exports.length === 1 ? '' : 's'}.
      </div>
    </div>
  );
}

function ExportStatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    pending: 'bg-slate-100 text-slate-700',
    in_flight: 'bg-blue-100 text-blue-800',
    succeeded: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    skipped: 'bg-amber-100 text-amber-800',
  };
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] ${colorMap[status] ?? ''}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export default CoLoopProjectMapping;
